import { useCallback, useEffect, useState } from 'react';
import { Plus, MapPin, Pencil, Trash2, Loader2, Check } from 'lucide-react';
import AddressForm, { emptyAddress, isAddressComplete } from './store/AddressForm';
import { StructuredAddress } from '../lib/storefront';
import {
  CustomerAddress,
  customerAddressLabel,
  customerAddressToStructured,
  deleteCustomerAddress,
  listCustomerAddresses,
  saveCustomerAddress,
  setDefaultShippingAddress,
} from '../lib/customerAddresses';

// ─────────────────────────────────────────────────────────────────────────────
// Delivery-address cards — the ONE staff UI for the customer address book.
//
//   pick   — order flows: cards are selectable (default pre-selected), “None +”
//            empty state, adding/editing a card also selects it. Every change
//            writes to customer_addresses, so the address is always retrievable
//            on the next order.
//   manage — customer profile: add/edit/delete/set-default, no selection.
// ─────────────────────────────────────────────────────────────────────────────

const inputClass =
  'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400';

export default function CustomerAddressCards({
  businessId,
  customerId,
  mode,
  selectedId,
  onSelect,
  areaPrefixes,
  prefillLine1,
}: {
  businessId: string;
  customerId: string;
  mode: 'pick' | 'manage';
  /** pick mode: currently selected card id (controlled by parent). */
  selectedId?: string | null;
  /** pick mode: fired with the card + its structured address (null = nothing usable). */
  onSelect?: (address: CustomerAddress | null, structured: StructuredAddress | null) => void;
  /** Delivery-area FSA prefixes for the in/out badge on the form. */
  areaPrefixes?: string[] | null;
  /** Seed for the add form when the book is empty (e.g. legacy customer.address text). */
  prefillLine1?: string | null;
}) {
  const [list, setList] = useState<CustomerAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [form, setForm] = useState<StructuredAddress>(emptyAddress());
  const [instructions, setInstructions] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listCustomerAddresses(customerId);
      setList(rows);
      // pick mode: auto-select the default (or only) card so repeat orders are
      // one tap — parent gets the structured address for pricing immediately.
      if (mode === 'pick' && onSelect && !selectedId) {
        const def = rows.find((r) => r.is_default_shipping) || rows[0] || null;
        onSelect(def, def ? customerAddressToStructured(def) : null);
      }
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setErr(null);
    const seed = emptyAddress();
    if (list.length === 0 && prefillLine1?.trim()) seed.line1 = prefillLine1.trim();
    setForm(seed);
    setInstructions('');
    setEditing('new');
  };

  const openEdit = (a: CustomerAddress) => {
    setErr(null);
    setForm(customerAddressToStructured(a));
    setInstructions(a.delivery_instructions || '');
    setEditing(a.id);
  };

  const handleSave = async () => {
    if (!isAddressComplete(form)) {
      setErr('Enter the street, city and a valid postal code.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const saved = await saveCustomerAddress(businessId, customerId, {
        ...(editing !== 'new' && editing ? { id: editing } : {}),
        address: form,
        deliveryInstructions: instructions,
      });
      const rows = await listCustomerAddresses(customerId);
      setList(rows);
      setEditing(null);
      if (mode === 'pick' && onSelect) {
        const fresh = rows.find((r) => r.id === saved.id) || saved;
        onSelect(fresh, customerAddressToStructured(fresh));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save the address');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a: CustomerAddress) => {
    if (!confirm('Delete this delivery address?')) return;
    try {
      await deleteCustomerAddress(customerId, a.id);
      const rows = await listCustomerAddresses(customerId);
      setList(rows);
      if (mode === 'pick' && onSelect && selectedId === a.id) {
        const def = rows.find((r) => r.is_default_shipping) || rows[0] || null;
        onSelect(def, def ? customerAddressToStructured(def) : null);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not delete the address');
    }
  };

  const handleMakeDefault = async (a: CustomerAddress) => {
    try {
      await setDefaultShippingAddress(customerId, a.id);
      setList(await listCustomerAddresses(customerId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not update the default');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-[12px] text-gray-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading addresses…
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Cards */}
      {list.map((a) => {
        const active = mode === 'pick' && selectedId === a.id;
        return (
          <div
            key={a.id}
            role={mode === 'pick' ? 'button' : undefined}
            onClick={mode === 'pick' ? () => onSelect?.(a, customerAddressToStructured(a)) : undefined}
            className={`rounded-xl border px-3.5 py-3 flex items-start gap-3 transition ${
              active
                ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500/30'
                : 'border-gray-200 bg-white'
            } ${mode === 'pick' ? 'cursor-pointer hover:border-blue-300' : ''}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {active ? <Check className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {(a.company || a.label) && (
                  <span className="text-[13px] font-semibold text-gray-900 truncate">{a.company || a.label}</span>
                )}
                {a.is_default_shipping && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    Default
                  </span>
                )}
              </div>
              <p className="text-[13px] text-gray-700 leading-snug">{customerAddressLabel(a)}</p>
              {a.delivery_instructions && (
                <p className="text-[11px] text-gray-500 mt-0.5 truncate">“{a.delivery_instructions}”</p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {mode === 'manage' && !a.is_default_shipping && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleMakeDefault(a); }}
                  className="px-2 py-1 rounded-lg text-[11px] font-semibold text-gray-500 hover:bg-gray-100"
                >
                  Make default
                </button>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                title="Edit address"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              {mode === 'manage' && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(a); }}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                  title="Delete address"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Empty state / add row */}
      {editing === null && (
        list.length === 0 ? (
          <button
            type="button"
            onClick={openAdd}
            className="w-full flex items-center justify-between rounded-xl border border-dashed border-gray-300 px-3.5 py-3 hover:border-blue-400 hover:bg-blue-50/30 transition"
          >
            <span className="flex items-center gap-2.5 text-[13px] text-gray-500">
              <MapPin className="w-4 h-4 text-gray-400" />
              Delivery address — <span className="font-medium">none</span>
            </span>
            <span className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center">
              <Plus className="w-4 h-4" />
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={openAdd}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:text-blue-700 px-1 py-0.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add delivery address
          </button>
        )
      )}

      {/* Add / edit form */}
      {editing !== null && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 space-y-2.5">
          <p className="text-[11px] font-bold text-blue-800 uppercase tracking-wide">
            {editing === 'new' ? 'New delivery address' : 'Edit delivery address'}
          </p>
          <AddressForm
            value={form}
            onChange={setForm}
            inputClass={inputClass}
            areaPrefixes={areaPrefixes ?? null}
            accent="#2563eb"
            idPrefix={`addr-book-${editing}`}
          />
          <input
            type="text"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Delivery instructions (gate code, where to leave it…) — optional"
            className={inputClass}
          />
          {err && <p className="text-[12px] font-medium text-red-600">{err}</p>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save address
            </button>
            <button
              type="button"
              onClick={() => { setEditing(null); setErr(null); }}
              className="px-3 py-2 rounded-xl text-[13px] font-semibold text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
          <p className="text-[11px] text-gray-500">
            Saved to the customer's profile — it will be preselected on their next order.
          </p>
        </div>
      )}
    </div>
  );
}
